import { useEffect, useMemo, useState } from 'react';
import { SampleDataRibbon } from './SampleDataRibbon';
import { TrendingUp, Globe, Filter, Search, Star, X, BarChart3, Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { cn } from './ui/utils';
import { useTrends } from '@/hooks/useTrends';
import { TrendCard } from './trends/TrendCard';
import { TrendDetailDrawer } from './trends/TrendDetailDrawer';
import { TrendRankingsView } from './trends/TrendRankingsView';
import type { TrendCategory, TrendStatus, Trend } from '@/utils/trend';
import { getPathForPage } from '@/routes';

// Top-level view switcher. URL param ?view=rankings persists the choice on reload
// and is the single source of truth so the back button works as expected.
type TrendsView = 'for_you' | 'rankings';
const TRENDS_VIEWS: { id: TrendsView; label: string; Icon: typeof BarChart3 }[] = [
  { id: 'for_you', label: 'Trends For You', Icon: Sparkles },
  { id: 'rankings', label: 'Trend Rankings', Icon: BarChart3 },
];

type TabType = TrendCategory | 'all';
const TABS: TabType[] = ['all', 'social', 'creator', 'content', 'marketing'];
const STATUSES: (TrendStatus | 'all')[] = ['all', 'emerging', 'active', 'peaking', 'declining'];

// Priority markets exposed in the selector regardless of what's in the fixture.
// SE first — Beatly's home market and the default country for TikTok Discovery.
const PRIORITY_GEOS = ['SE', 'US', 'GB', 'BR', 'DE', 'FR', 'MX'];
const GEO_LABELS: Record<string, string> = {
  SE: 'Sweden', US: 'United States', GB: 'United Kingdom', BR: 'Brazil',
  DE: 'Germany', FR: 'France', MX: 'Mexico', global: 'Global', team: 'Your team',
};
function geoLabel(code: string): string {
  if (code === 'all') return 'All markets';
  return GEO_LABELS[code] ?? code;
}

// localStorage keys are namespaced by feature so other Trend surfaces (widgets,
// future campaign-brief surfaces) can share or replace this state safely.
const SAVED_QUERIES_KEY = 'beatly.trends.savedQueries';
const MAX_SAVED_QUERIES = 8;

interface SavedQuery {
  query: string;
  savedAt: string; // ISO
}

function readSavedQueries(): SavedQuery[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(SAVED_QUERIES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((x): x is SavedQuery => !!x && typeof x.query === 'string' && x.query.trim().length > 0)
      .slice(0, MAX_SAVED_QUERIES);
  } catch {
    return [];
  }
}

function writeSavedQueries(list: SavedQuery[]) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SAVED_QUERIES_KEY, JSON.stringify(list.slice(0, MAX_SAVED_QUERIES)));
  } catch {
    // Quota / private-mode errors aren't fatal — we just lose persistence.
  }
}

export interface TrendsPageProps {
  navigate?: (path: string) => void;
}

export function TrendsPage({ navigate }: TrendsPageProps = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [status, setStatus] = useState<TrendStatus | 'all'>('all');
  const [geo, setGeo] = useState<string>('SE');
  const [selected, setSelected] = useState<Trend | null>(null);

  // Active search query — seeded from ?q= so reload/share preserves it. Two-way
  // sync: typing updates the URL; URL changes (back/forward, chip click) update
  // the input. URL is the source of truth.
  const urlQuery = searchParams.get('q') ?? '';
  const [query, setQuery] = useState<string>(urlQuery);
  useEffect(() => {
    if (query !== urlQuery) setQuery(urlQuery);
  }, [urlQuery]);
  const commitQuery = (next: string) => {
    setQuery(next);
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next.trim()) p.set('q', next.trim());
        else p.delete('q');
        return p;
      },
      { replace: true }
    );
  };

  // Saved queries persist in localStorage and survive reloads. Not per-user
  // (no backend) — upgrade path when we ship saved-searches API is to swap the
  // read/write helpers for fetch calls without touching this component.
  const [savedQueries, setSavedQueries] = useState<SavedQuery[]>(() => readSavedQueries());
  useEffect(() => {
    writeSavedQueries(savedQueries);
  }, [savedQueries]);
  const isSaved = savedQueries.some((s) => s.query === query.trim());
  const toggleSaveCurrent = () => {
    const q = query.trim();
    if (!q) return;
    setSavedQueries((prev) =>
      prev.some((s) => s.query === q)
        ? prev.filter((s) => s.query !== q)
        : [{ query: q, savedAt: new Date().toISOString() }, ...prev].slice(0, MAX_SAVED_QUERIES)
    );
  };
  const removeSaved = (q: string) => setSavedQueries((prev) => prev.filter((s) => s.query !== q));

  const { data: trends, raw, rankings, metadata, isLoading } = useTrends({
    category: activeTab,
    status,
    geo,
    query,
    limit: 24,
  });

  // URL-synced top-level view. Default = for_you so existing links keep working.
  const urlView = searchParams.get('view');
  const activeView: TrendsView = urlView === 'rankings' ? 'rankings' : 'for_you';
  const setActiveView = (next: TrendsView) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === 'rankings') p.set('view', 'rankings');
        else p.delete('view');
        return p;
      },
      { replace: true }
    );
  };

  const geoOptions = useMemo(() => {
    const fromData = new Set<string>();
    (raw ?? []).forEach((t) => {
      if (t.geo_scope && t.geo_scope !== 'global') fromData.add(t.geo_scope);
    });
    // Priority markets always visible; data-driven markets appended if new.
    const extras = Array.from(fromData).filter((c) => !PRIORITY_GEOS.includes(c)).sort();
    return ['all', 'global', ...PRIORITY_GEOS, ...extras];
  }, [raw]);

  const openTrend = (t: Trend) => setSelected(t);
  const applyTrend = (t: Trend) => {
    const path = getPathForPage('campaigns');
    navigate?.(`${path}/new?trend=${encodeURIComponent(t.slug)}`);
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-20">
      <SampleDataRibbon />
      <div className="mx-auto max-w-[1400px] p-8">
        {/* Header */}
        <div className="mb-8 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <div className="rounded-xl bg-[#2e024c] p-2">
                  <TrendingUp className="h-5 w-5 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-[#101828]">Trend Radar</h1>
              </div>
              <p className="text-sm text-gray-600">
                Personalised signals from the creator economy, cultural shifts, and your own paid performance.
              </p>
              <div className="mt-2">
                {metadata.source === 'live' ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                    <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                    Live · {metadata.generatedAt ? new Date(metadata.generatedAt).toLocaleString() : 'just now'}
                    {metadata.pipelineSources && metadata.pipelineSources.length > 0 && (
                      <span className="font-normal opacity-70">
                        · {metadata.pipelineSources.join(' + ')}
                      </span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-[11px] font-semibold text-gray-500">
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-gray-400" />
                    Sample data · run bin/generate-trends to wire real signals
                  </span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-1.5 text-[12px] text-gray-600">
                <Globe className="h-3.5 w-3.5" />
                <select
                  className="bg-transparent text-[12px] outline-none"
                  value={geo}
                  onChange={(e) => setGeo(e.target.value)}
                  aria-label="Geography filter"
                >
                  {geoOptions.map((g) => (
                    <option key={g} value={g}>
                      {geoLabel(g)}
                    </option>
                  ))}
                </select>
              </div>
              {/* Status/phase filter is a Gemini-synthesized attribute — hide it in Rankings. */}
              {activeView === 'for_you' && (
                <div className="flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-2 py-1.5 text-[12px] text-gray-600">
                  <Filter className="h-3.5 w-3.5" />
                  <select
                    className="bg-transparent text-[12px] outline-none"
                    value={status}
                    onChange={(e) => setStatus(e.target.value as TrendStatus | 'all')}
                    aria-label="Status filter"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {s === 'all' ? 'All phases' : s.charAt(0).toUpperCase() + s.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* View switcher — Trends For You (AI-synthesized) vs Trend Rankings (raw source lists). */}
          <div className="mt-5 inline-flex gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-1">
            {TRENDS_VIEWS.map((v) => {
              const Icon = v.Icon;
              const active = activeView === v.id;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setActiveView(v.id)}
                  className={cn(
                    'flex items-center gap-2 rounded-xl px-4 py-2 text-[13px] font-medium transition-all',
                    active
                      ? 'bg-white text-[#2e024c] shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  )}
                >
                  <Icon className={cn('h-3.5 w-3.5', active ? 'text-[#be43ff]' : '')} />
                  {v.label}
                </button>
              );
            })}
          </div>

          <div className="mt-5 flex flex-col gap-3">
            {/* Search — filters by topic or hashtag across title, description, hashtags,
                keywords, and every signal's raw_data. Active query lives in ?q=. */}
            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-1 min-w-[240px] items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-[13px]">
                <Search className="h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => commitQuery(e.target.value)}
                  placeholder="Filter loaded trends by topic or hashtag…"
                  aria-label="Filter loaded trends by topic or hashtag"
                  className="flex-1 bg-transparent outline-none placeholder:text-gray-400"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => commitQuery('')}
                    aria-label="Clear search"
                    className="rounded-md p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={toggleSaveCurrent}
                  disabled={!query.trim()}
                  aria-label={isSaved ? 'Unsave this query' : 'Save this query'}
                  title={isSaved ? 'Unsave this query' : 'Save this query'}
                  className={cn(
                    'rounded-md p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-30',
                    isSaved ? 'text-[#be43ff]' : 'text-gray-400 hover:text-[#be43ff]'
                  )}
                >
                  <Star className={cn('h-4 w-4', isSaved && 'fill-[#be43ff]')} />
                </button>
              </div>
            </div>
            {savedQueries.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
                  Saved
                </span>
                {savedQueries.map((s) => (
                  <span
                    key={s.query}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[12px] transition-colors',
                      s.query === query.trim()
                        ? 'border-[#be43ff]/40 bg-[#f3ecff] text-[#6b1f94]'
                        : 'border-gray-200 bg-white text-gray-600 hover:border-[#be43ff]/40 hover:text-[#6b1f94]'
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => commitQuery(s.query)}
                      className="text-inherit"
                    >
                      {s.query}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeSaved(s.query)}
                      aria-label={`Remove saved query ${s.query}`}
                      className="-mr-1 rounded-full p-0.5 text-inherit opacity-60 hover:opacity-100"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Category tabs — only meaningful for the AI-synthesized "For You" view. */}
          {activeView === 'for_you' && (
            <div className="mt-3 flex flex-wrap gap-1 rounded-2xl border border-gray-200 bg-gray-50 p-1.5">
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    'rounded-xl px-5 py-2 text-sm font-medium transition-all',
                    activeTab === tab
                      ? 'bg-white text-[#be43ff] shadow-sm'
                      : 'text-gray-500 hover:text-gray-900'
                  )}
                >
                  {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          )}
        </div>

        {activeView === 'rankings' ? (
          <TrendRankingsView rankings={rankings} geo={geo} query={query} />
        ) : isLoading && !trends.length ? (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-[260px] rounded-3xl bg-white shadow-sm" />
            ))}
          </div>
        ) : trends.length === 0 ? (
          <EmptyTrends query={query} onClearQuery={() => commitQuery('')} />
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
            {trends.map((trend) => (
              <TrendCard
                key={trend.id}
                trend={trend}
                onOpen={openTrend}
                onApply={applyTrend}
              />
            ))}
          </div>
        )}

      </div>

      <TrendDetailDrawer
        trend={selected}
        open={Boolean(selected)}
        onOpenChange={(open) => !open && setSelected(null)}
        onApply={applyTrend}
      />
    </div>
  );
}

/**
 * Empty state that tells the user the truth: search filters the CURRENT feed,
 * it doesn't fetch fresh signals. When a query is active we surface the exact
 * CLI command to regenerate with that keyword as a seed, with a copy button so
 * the loop back to the terminal is one click.
 */
function EmptyTrends({ query, onClearQuery }: { query: string; onClearQuery: () => void }) {
  const trimmed = query.trim();
  const [copied, setCopied] = useState(false);
  if (!trimmed) {
    return (
      <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-10 text-center">
        <h3 className="text-[15px] font-semibold text-[#344054]">No trends match those filters</h3>
        <p className="mt-2 text-[13px] text-gray-500">
          Reset the tab, geography, or phase — or come back after the next refresh cycle.
        </p>
      </div>
    );
  }
  const cliCommand = `./bin/generate-trends.cjs --jwt=$BEATLY_API_TOKEN --seed="${trimmed}"`;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cliCommand);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked — user can still select manually.
    }
  };
  return (
    <div className="rounded-3xl border border-dashed border-gray-200 bg-white p-10 text-center">
      <h3 className="text-[15px] font-semibold text-[#344054]">
        No loaded trend matches “{trimmed}”
      </h3>
      <p className="mx-auto mt-2 max-w-[520px] text-[13px] leading-relaxed text-gray-500">
        This bar filters the current feed only — it doesn't pull fresh signals. To regenerate
        trends seeded with this keyword, run the CLI:
      </p>
      <div className="mx-auto mt-4 flex max-w-[560px] items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-left font-mono text-[12px] text-gray-700">
        <code className="flex-1 overflow-x-auto whitespace-nowrap">{cliCommand}</code>
        <button
          type="button"
          onClick={copy}
          className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-medium text-gray-700 hover:border-[#be43ff]/40 hover:text-[#6b1f94]"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <button
        type="button"
        onClick={onClearQuery}
        className="mt-4 text-[12px] font-medium text-[#be43ff] hover:underline"
      >
        Clear search and show all loaded trends
      </button>
    </div>
  );
}
